import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * Gift add-on: adds a single gift bag at full price ($8) to the cart.
 * Gift items are linked to the main product variant and auto-removed when it's deleted.
 * @extends {Component}
 */
class ProductGiftAddon extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.#giftCheckbox = this.querySelector('.product-gift-addon__checkbox');
    this.#giftCheckbox?.addEventListener('change', this.#handleGiftToggle);

    this.#hookIntoAddToCartButton(signal);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd, { signal });
    document.addEventListener(CartUpdateEvent.eventName, this.#handleCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#giftCheckbox?.removeEventListener('change', this.#handleGiftToggle);
  }

  #controller = new AbortController();
  #giftCheckbox = null;
  #addToCartButton = null;
  #pendingGiftAdd = false;

  #hookIntoAddToCartButton(signal) {
    const tryHook = () => {
      const section = this.closest('.shopify-section');
      if (!section) return;

      const form = section.querySelector('product-form-component form');
      if (!form || form === this.#addToCartButton) return;

      this.#addToCartButton = form;

      section.addEventListener(
        CartAddEvent.eventName,
        async (event) => {
          if (event.detail?.data?.source !== 'product-form-component') return;
          if (event.detail?.data?.didError) return;
          if (this.hidden) return;
          if (!(this.#giftCheckbox instanceof HTMLInputElement) || !this.#giftCheckbox.checked) return;
          if (this.#pendingGiftAdd) return;
          this.#pendingGiftAdd = true;

          const sections = event.detail?.data?.sections ? Object.keys(event.detail.data.sections) : this.#getSectionIds();
          await this.#addGiftToCart(sections);

          setTimeout(() => { this.#pendingGiftAdd = false; }, 500);
        },
        { signal }
      );
    };

    tryHook();
    setTimeout(tryHook, 300);
  }

  #handleGiftToggle = () => {
    const isChecked = this.#giftCheckbox instanceof HTMLInputElement && this.#giftCheckbox.checked;
    this.classList.toggle('is-selected', isChecked);
  };

  #getGiftVariantId() {
    return Number(this.dataset.defaultGiftVariantId);
  }

  #getBonusVariantId() {
    return Number(this.dataset.bonusVariantId);
  }

  #getBonusQuantity() {
    return Number(this.dataset.bonusQuantity) || 2;
  }

  #getLinkedVariantId() {
    return this.dataset.linkedVariantId || '';
  }

  #getSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.add(el.dataset.sectionId);
      }
    });
    return Array.from(ids);
  }

  async #addGiftToCart(sections) {
    const giftVariantId = this.#getGiftVariantId();
    if (!giftVariantId) return;

    const linkedVariantId = this.#getLinkedVariantId();
    const linkedProp = linkedVariantId ? { _linked_variant: linkedVariantId } : {};

    const bonusVariantId = this.#getBonusVariantId();
    const bonusQuantity = this.#getBonusQuantity();

    const items = [
      {
        id: giftVariantId,
        quantity: 1,
        properties: linkedProp,
      },
    ];

    if (bonusVariantId) {
      items.push({
        id: bonusVariantId,
        quantity: bonusQuantity,
        properties: { _free_gift: 'true', ...linkedProp },
      });
    }

    const body = JSON.stringify({
      items,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.status) {
        console.warn('Gift add-on failed:', data.message || data.description);
        return;
      }

      sections.forEach((sectionId) => {
        if (data.sections?.[sectionId]) {
          morphSection(sectionId, data.sections[sectionId]);
        }
      });
    } catch (err) {
      console.error('Gift add-on cart error:', err);
    }
  }

  /** When main product is removed from cart, auto-remove linked gift items */
  #handleCartUpdate = async (event) => {
    if (event.detail?.data?.source === 'product-gift-addon') return;
    if (event.detail?.data?.source !== 'cart-items-component') return;

    const cart = event.detail?.resource;
    if (!cart?.items) return;

    const cartVariantIds = new Set(cart.items.map((i) => String(i.variant_id)));

    const linesToRemove = cart.items
      .filter((item) => {
        const linked = item.properties?._linked_variant;
        return linked && !cartVariantIds.has(String(linked));
      })
      .map((item) => item.key);

    if (linesToRemove.length === 0) return;

    const updates = {};
    for (const key of linesToRemove) {
      updates[key] = 0;
    }

    try {
      const sections = this.#getSectionIds();
      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      }));
      const data = await response.json();
      if (data.token) {
        sections.forEach((sectionId) => {
          if (data.sections?.[sectionId]) {
            morphSection(sectionId, data.sections[sectionId]);
          }
        });
        document.dispatchEvent(new CartUpdateEvent(data, '', { source: 'product-gift-addon', sections: data.sections }));
      }
    } catch (err) {
      console.error('Gift linked-item removal error:', err);
    }
  };

  #handleCartAdd = async (event) => {
    if (event.detail?.data?.source !== 'product-form-component') return;
    if (event.detail?.data?.didError) return;
    if (this.hidden) return;
    if (!(this.#giftCheckbox instanceof HTMLInputElement) || !this.#giftCheckbox.checked) return;
    if (this.#pendingGiftAdd) return;

    this.#pendingGiftAdd = true;
    const sections = event.detail?.data?.sections ? Object.keys(event.detail.data.sections) : this.#getSectionIds();
    await this.#addGiftToCart(sections);
    setTimeout(() => { this.#pendingGiftAdd = false; }, 500);
  };
}

if (!customElements.get('product-gift-addon')) {
  customElements.define('product-gift-addon', ProductGiftAddon);
}
